import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, useHistory, Redirect } from 'react-router-dom';
import { Subheader } from '@m12s/component-library';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';

import { Downtime } from 'components/Downtime';
import { Alarms } from 'components/Alarms';
import { Utilization } from 'components/Utilization';
import MainLayout from 'components/Layouts/MainLayout';
import { CycleTime } from 'components/CycleTime';
import { CycleCountStatus } from 'components/CycleCountStatus';
import { MachineStatus } from 'components/MachineStatus';
import { OEE } from 'components/OEE';
import { PartsGoal } from 'components/PartsGoal';
import Routes from 'lib/utils/routes';
import { CustomTab } from 'components/CustomTab';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getHomeTabRoute, getVisibleTabs } from 'lib/selectors/getTabs';
import { getCustomTabs } from 'lib/selectors/getCustomTab';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getIsWaitingForWork } from 'lib/selectors/getIsWaitingForWork';
import { getIsWaitingForOperator } from 'lib/selectors/getIsWaitingForOperator';
import { getIsUnknownJob } from 'lib/selectors/getIsUnknownJob';
import { getHasCards } from 'lib/selectors/getHasCards';
import { APM_OPDASH_HOST } from 'lib/appConfig';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import {
  actionOpenLaborTicketModal,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { getFAIBannerVisible } from 'lib/selectors/getFAIBannerVisible';
import FAIBanner from 'components/FirstArticleInspection/FAIBanner';
import { SingleSelectJobDrawer } from 'components/SingleSelectJobDrawer';
import WaitingForWork from './MachineMainWrapper/WaitingForWork';
import WaitingForOperator from './MachineMainWrapper/WaitingForOperator';
import UnknownJob from './MachineMainWrapper/UnknownJob';
import Tab from './Tab';

const ContentContainer = styled(MainLayout.Container)`
  background-color: #fafbfc;
`;

const MachineContent = ({ tab, openSelectJobDrawer }) => {
  const machine = useSelector(getMachine);
  const scopeStart = useSelector(getScopeStart);
  const latestOperator = useSelector(getLatestOperator);
  const latestOperatorErpId = latestOperator?.erpId;
  const dispatch = useDispatch();
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const isWaitingForWork = useSelector(getIsWaitingForWork);
  const isWaitingForOperator = useSelector(getIsWaitingForOperator);
  const isUnknownJob = useSelector(getIsUnknownJob);
  const hasCards = useSelector(getHasCards);

  const [refreshToken, setRefreshToken] = useState(0);
  const [dismissedItemIds, setDismissedItemIds] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.dismissItemId;
      if (id) {
        setDismissedItemIds((prev) => {
          return [...prev, id];
        });
      }
      setRefreshToken((t) => {
        return t + 1;
      });
    };
    window.addEventListener('apm:opdash-refresh', handler);
    return () => {
      window.removeEventListener('apm:opdash-refresh', handler);
    };
  }, []);

  // Run Details flyup state
  const [runDetailsOpen, setRunDetailsOpen] = useState(false);
  const [runDetailsContext, setRunDetailsContext] = useState(null);

  // select-different-job: user picks a different WOO (from NO_MATCH or SUGGESTED)
  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:select-different-job',
    useCallback(
      (data) => {
        openSelectJobDrawer(
          data?.eventId ? { eventId: data.eventId, isChangeJob: true } : null
        );
      },
      [openSelectJobDrawer]
    )
  );

  // Listen for CONFIRMED card tap → open run details flyup
  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:view-run-details',
    useCallback((data) => {
      setRunDetailsContext(data);
      setRunDetailsOpen(true);
    }, [])
  );

  // Listen for close from run details MFE
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:close',
    useCallback(() => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
    }, [])
  );

  // Listen for confirmed from run details → close and refresh opdash cards
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:confirmed',
    useCallback(() => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      setRefreshToken((t) => {
        return t + 1;
      });
    }, [])
  );

  // Listen for change-job from run details → close details, open select-job drawer (merge flow)
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:change-job',
    useCallback(
      (data) => {
        setRunDetailsOpen(false);
        setRunDetailsContext(null);
        openSelectJobDrawer(
          data?.eventId ? { eventId: data.eventId, isChangeJob: true } : null
        );
      },
      [openSelectJobDrawer]
    )
  );

  // Listen for labor ticket click from run details → close details, open LT form
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:open-ticket',
    useCallback(
      (data) => {
        if (data?.laborTicketRef) {
          setRunDetailsOpen(false);
          setRunDetailsContext(null);
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: latestOperatorErpId,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
        }
      },
      [dispatch, latestOperatorErpId]
    )
  );

  // Split-change-job: open select-job drawer with split context
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-change-job',
    useCallback(
      (data) => {
        setRunDetailsOpen(false);
        setRunDetailsContext(null);
        openSelectJobDrawer({ isSplitChangeJob: true }, data);
      },
      [openSelectJobDrawer]
    )
  );

  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-submit',
    useCallback(
      (data) => {
        setRunDetailsOpen(false);
        setRunDetailsContext(null);
        setRefreshToken((t) => {
          return t + 1;
        });

        if (data?.laborTicketRef) {
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: latestOperatorErpId,
              splitTime: data.splitTime,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
        }
      },
      [dispatch, latestOperatorErpId]
    )
  );

  const tabIdentifier = tab.id;
  switch (tabIdentifier) {
    case 'cycleCount':
      return <CycleCountStatus />;
    case 'jobs': {
      if (isApmERPJobFlow && !hasCards) {
        if (isUnknownJob) {
          return <UnknownJob />;
        }
        if (!currentWorkOrderOp) {
          if (isWaitingForWork) return <WaitingForWork />;
          if (isWaitingForOperator) return <WaitingForOperator />;
        }
      }
      return (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <MMEmbeddableZoneWithLoader
            id="apm-opdash-display"
            src={APM_OPDASH_HOST}
            path="/"
            context={{
              machineRef: machine?.machineRef,
              machineId: machine?.id,
              start: scopeStart,
              erpResources: machine?.erpResources,
              refreshToken,
              dismissedItemIds,
              personId: latestOperatorErpId,
            }}
          />
          <MMDrawer
            isOpen={runDetailsOpen}
            onClose={() => {
              setRunDetailsOpen(false);
              setRunDetailsContext(null);
            }}
            title="Production Run Details"
          >
            <MMEmbeddableZoneWithLoader
              id="apm-opdash-run-details"
              src={APM_OPDASH_HOST}
              path="/run-details"
              context={{
                machineRef: machine?.machineRef,
                start: scopeStart,
                erpResources: machine?.erpResources,
                personId: latestOperatorErpId,
                ...runDetailsContext,
              }}
            />
          </MMDrawer>
        </div>
      );
    }
    case 'partCount':
      return <MachineStatus />;
    case 'utilization':
      return <Utilization />;
    case 'downtime':
      return <Downtime />;
    case 'alarms':
      return <Alarms />;
    case 'partsGoal':
      return <PartsGoal />;
    case 'cycleTime':
      return <CycleTime />;
    case 'oee':
      return <OEE />;
    default:
      return null;
  }
};

const MachineMain = ({ tab: currentTab }) => {
  const history = useHistory();
  const machine = useSelector(getMachine);
  const baseTabs = useSelector(getVisibleTabs);
  const scopeStart = useSelector(getScopeStart);
  const baseHomeTabRoute = useSelector(getHomeTabRoute);
  const machineId = machine?.id;
  const customTabs = useSelector(getCustomTabs);
  const faiBannerVisible = useSelector(getFAIBannerVisible);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const currentOperator = useSelector(getCurrentOperator);
  const windowPath = window.location.pathname;

  // --- Single-select job drawer state (lives here so it survives tab switches) ---
  const [selectJobDrawerOpen, setSelectJobDrawerOpen] = useState(false);
  const [drawerApmFlow, setDrawerApmFlow] = useState(null);
  const [drawerSplitContext, setDrawerSplitContext] = useState(null);

  const openSelectJobDrawer = useCallback((navState, splitPayload) => {
    setDrawerApmFlow(navState || null);
    setDrawerSplitContext(splitPayload || null);
    setSelectJobDrawerOpen(true);
  }, []);

  const completeSelectJobDrawer = useCallback(() => {
    setSelectJobDrawerOpen(false);
    setDrawerApmFlow(null);
    setDrawerSplitContext(null);
    window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
  }, []);

  const selectJobFlowContextValue = useMemo(() => {
    return {
      apmFlowState: drawerApmFlow,
      splitJobContext: drawerSplitContext,
      onApmFlowComplete: completeSelectJobDrawer,
    };
  }, [drawerApmFlow, drawerSplitContext, completeSelectJobDrawer]);

  useEffect(() => {
    if (isApmERPJobFlow && !currentOperator && selectJobDrawerOpen) {
      completeSelectJobDrawer();
    }
  }, [
    isApmERPJobFlow,
    currentOperator,
    selectJobDrawerOpen,
    completeSelectJobDrawer,
  ]);

  useEffect(() => {
    const handler = () => {
      if (isApmERPJobFlow && !currentOperator) {
        return;
      }
      openSelectJobDrawer(null);
    };
    window.addEventListener('apm:open-select-job-drawer', handler);
    return () => {
      window.removeEventListener('apm:open-select-job-drawer', handler);
    };
  }, [openSelectJobDrawer, isApmERPJobFlow, currentOperator]);

  // For APM ERP machines, inject a "Jobs" tab at the front of the list
  const tabs = useMemo(() => {
    if (isApmERPJobFlow) {
      return [{ id: 'jobs', enabled: true }, ...baseTabs];
    }
    return baseTabs;
  }, [baseTabs, isApmERPJobFlow]);

  // Recompute home route based on the (possibly augmented) tab list
  const homeTabRoute = useMemo(() => {
    if (tabs.length && machineId) {
      return Routes.machineIdHomeTabPath(machineId, tabs[0].id);
    }
    return baseHomeTabRoute;
  }, [tabs, machineId, baseHomeTabRoute]);

  if (!scopeStart || !homeTabRoute) {
    return null;
  }

  if (faiBannerVisible) {
    return <FAIBanner />;
  }

  return (
    <>
      <Subheader
        defaultValue={currentTab}
        onChange={(value) => {
          return history.push(Routes.machineIdHomeTabPath(machine.id, value));
        }}
      >
        {tabs.length > 1 &&
          tabs.map((tab) => {
            return <Tab key={tab.id} tab={tab} />;
          })}
      </Subheader>
      <ContentContainer>
        {customTabs.map((customTab) => {
          return (
            <CustomTab
              key={customTab.id}
              tab={customTab}
              base={Routes.machineIdHomeTabPath(machine.id, customTab.id)}
              hidden={
                windowPath !==
                Routes.machineIdHomeTabPath(machine.id, customTab.id)
              }
            />
          );
        })}
        <Switch>
          {tabs.map((tab) => {
            return (
              <Route
                key={tab.id}
                path={Routes.machineIdHomeTabPath(machine.id, tab.id)}
              >
                <MachineContent
                  tab={tab}
                  openSelectJobDrawer={openSelectJobDrawer}
                />
              </Route>
            );
          })}
          <Route path={Routes.machineIdHomePath(machine.id)}>
            <Redirect to={homeTabRoute} />
          </Route>
        </Switch>
      </ContentContainer>
      {isApmERPJobFlow && (
        <SingleSelectJobDrawer
          isOpen={selectJobDrawerOpen}
          onClose={completeSelectJobDrawer}
          flowContextValue={selectJobFlowContextValue}
        />
      )}
    </>
  );
};

MachineMain.propTypes = {
  tab: PropTypes.string.isRequired,
};

export default MachineMain;
